import { loadLeaderboard, submitScore, type LeaderboardEntry } from "../api";
import { ROUND_COMPLETE_EVENT, ROUND_EXIT_EVENT, ROUND_READY_EVENT, type RoundResult } from "../game/types";
import { SoundBus } from "../game/sound";
import type { GameTheme } from "../theme/types";

type OverlayOptions = {
  root: HTMLElement;
  theme: GameTheme;
  startRound: () => void;
  hidden?: boolean;
};

export function initOverlay({ root, theme, startRound, hidden = false }: OverlayOptions) {
  applyTheme(root, theme);

  let latestResult: RoundResult | undefined;
  let entries: LeaderboardEntry[] = [];
  let loadingStartedAt = 0;
  let loadingToken = 0;
  let menuHoleTimers: number[] = [];
  let audioUnlocked = false;
  let unlockTimer: number | undefined;
  let menuUnlockHandler: (() => void) | undefined;
  const soundBus = new SoundBus(theme);

  function clearMenuHoleTimers() {
    for (const timer of menuHoleTimers) {
      window.clearTimeout(timer);
    }
    menuHoleTimers = [];

    if (unlockTimer !== undefined) {
      window.clearTimeout(unlockTimer);
      unlockTimer = undefined;
    }

    if (menuUnlockHandler) {
      window.removeEventListener("pointerdown", menuUnlockHandler, { capture: true });
      window.removeEventListener("keydown", menuUnlockHandler, { capture: true });
      menuUnlockHandler = undefined;
    }
  }

  function renderLoading() {
    clearMenuHoleTimers();
    loadingStartedAt = performance.now();
    loadingToken += 1;
    root.className = "overlay overlay-loading";
    root.innerHTML = `
      <section class="original-screen loading-screen" aria-label="Loading"></section>
    `;
  }

  function renderMenu() {
    clearMenuHoleTimers();
    root.className = "overlay overlay-menu";
    root.innerHTML = `
      <section class="original-screen menu-screen" aria-label="${theme.title}">
        <div class="title-logo" aria-label="${theme.title}">
          <span class="presenter-copy">Gruschelmedia<br />presents</span>
          <span class="css-title-logo" aria-label="Moormenners">Moormenners</span>
        </div>
        ${renderMenuTargetDetail(theme)}
        <button class="image-button menu-start menu-art-text" data-action="start">
          ${theme.ui.copy.start}
        </button>
        <button class="image-button menu-highscore menu-art-text" data-action="highscore">
          Highscore
        </button>
        <span class="menu-version">${renderBitmapText("v13.12", theme)}</span>
      </section>
    `;

    soundBus.playLoop("music");
    bindSharedActions();
    animateMenuHoles();
  }

  function renderHighscore() {
    clearMenuHoleTimers();
    const displayEntries = effectiveLeaderboardEntries(theme, entries);

    root.className = "overlay overlay-highscore";
    root.innerHTML = `
      <section class="original-screen highscore-screen" aria-label="${theme.ui.copy.leaderboard}">
        <div class="highscore-list">
          ${displayEntries.map((entry) => renderEndRow(theme, entry.name, entry.score)).join("")}
        </div>
        <button class="text-image-button highscore-menu menu-art-text" data-action="menu" aria-label="Menu">
          <span>MENU</span>
        </button>
      </section>
    `;

    soundBus.playLoop("music");
    bindSharedActions();
  }

  function renderPlaying() {
    clearMenuHoleTimers();
    soundBus.stopAllLoops();
    root.className = "overlay overlay-hidden";
    root.innerHTML = "";
  }

  function renderGameOver(result: RoundResult, submitState: "idle" | "submitting" | "done" | "error" = "idle") {
    clearMenuHoleTimers();
    const lastName = localStorage.getItem("moormenners:name") ?? "";
    const displayEntries = effectiveLeaderboardEntries(theme, entries);
    const rank = rankForResult(displayEntries, result);
    const qualifies = rank <= 10;
    soundBus.stopAllLoops();
    root.className = "overlay overlay-game-over";
    root.innerHTML = `
      <section class="original-screen highscore-screen" aria-label="${theme.ui.copy.gameOver}">
        <div class="highscore-list">
          ${renderEndLeaderboard(theme, displayEntries, result, rank, qualifies, lastName, submitState)}
        </div>
        ${submitState === "error" ? `<p class="form-error">${renderBitmapText("Keine Verbindung zum Server", theme)}</p>` : ""}
        <button class="text-image-button highscore-menu menu-art-text" data-action="menu" aria-label="Menu">
          <span>MENU</span>
        </button>
      </section>
    `;

    bindSharedActions();
    bindScoreForm(result);
  }

  function bindSharedActions() {
    root.querySelector<HTMLElement>('[data-action="start"]')?.addEventListener("click", () => {
      soundBus.play("button");
      soundBus.stopAllLoops();
      renderLoading();
      startRound();
    });

    root.querySelector<HTMLElement>('[data-action="restart"]')?.addEventListener("click", () => {
      soundBus.play("button");
      soundBus.stopAllLoops();
      renderLoading();
      startRound();
    });

    root.querySelector<HTMLElement>('[data-action="menu"]')?.addEventListener("click", () => {
      soundBus.play("button");
      latestResult = undefined;
      void refreshLeaderboard().then(renderMenu);
    });

    root.querySelector<HTMLElement>('[data-action="highscore"]')?.addEventListener("click", async () => {
      soundBus.play("button");
      await refreshLeaderboard();
      renderHighscore();
    });

  }

  function animateMenuHoles() {
    const holes = [...root.querySelectorAll<HTMLElement>(".menu-target-hole")];
    let ran = false;
    const run = () => {
      if (ran) return;
      ran = true;
      audioUnlocked = true;
      if (menuUnlockHandler) {
        window.removeEventListener("pointerdown", menuUnlockHandler, { capture: true });
        window.removeEventListener("keydown", menuUnlockHandler, { capture: true });
      }
      menuUnlockHandler = undefined;

      soundBus.playLoop("music");

      holes.forEach((hole, index) => {
        const timer = window.setTimeout(() => {
          hole.classList.add("menu-target-hole-visible");
          soundBus.play("shoot");
        }, index * 115);

        menuHoleTimers.push(timer);
      });
    };

    if (audioUnlocked) {
      unlockTimer = window.setTimeout(run, 180);
      return;
    }

    menuUnlockHandler = () => run();
    window.addEventListener("pointerdown", menuUnlockHandler, { once: true, capture: true });
    window.addEventListener("keydown", menuUnlockHandler, { once: true, capture: true });
  }

  function bindScoreForm(result: RoundResult) {
    const form = root.querySelector<HTMLFormElement>("[data-score-form]");
    if (!form) return;

    bindNameEntry(form, theme);

    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      const formData = new FormData(form);
      const name = String(formData.get("name") ?? "").trim();
      if (!name) return;

      soundBus.play("stamp");
      localStorage.setItem("moormenners:name", name);
      renderGameOver(result, "submitting");

      try {
        await submitScore(name, result);
        await refreshLeaderboard();
        renderGameOver(result, "done");
      } catch {
        renderGameOver(result, "error");
      }
    });
  }

  async function refreshLeaderboard() {
    try {
      entries = await loadLeaderboard(theme.id, 20);
    } catch {
      entries = [];
    }
  }

  window.addEventListener(ROUND_COMPLETE_EVENT, (event) => {
    latestResult = (event as CustomEvent<RoundResult>).detail;
    void refreshLeaderboard().then(() => renderGameOver(latestResult as RoundResult));
  });

  window.addEventListener(ROUND_EXIT_EVENT, () => {
    latestResult = undefined;
    void refreshLeaderboard().then(renderMenu);
  });

  window.addEventListener(ROUND_READY_EVENT, () => {
    const token = loadingToken;
    const elapsed = performance.now() - loadingStartedAt;
    const delay = Math.max(0, 220 - elapsed);

    window.setTimeout(() => {
      window.requestAnimationFrame(() => {
        if (token === loadingToken && root.classList.contains("overlay-loading")) {
          renderPlaying();
        }
      });
    }, delay);
  });

  if (hidden) {
    renderPlaying();
    return;
  }

  renderLoading();
  void refreshLeaderboard().then(renderMenu);
}

function bindNameEntry(form: HTMLFormElement, theme: GameTheme) {
  const field = form.querySelector<HTMLElement>("[data-name-entry]");
  const input = form.querySelector<HTMLInputElement>('input[name="name"]');
  if (!field || !input || field.dataset.disabled === "true") return;

  const nameField = field;
  const nameInput = input;
  let value = input.value.slice(0, 24);
  let submitted = false;

  function submit() {
    if (submitted || !value.trim()) return;
    submitted = true;
    form.requestSubmit();
  }

  function sync() {
    nameInput.value = value;
    nameField.innerHTML = renderBitmapText(value || "_", theme);
    nameField.setAttribute("aria-label", value ? `Name ${value}` : "Name");
  }

  nameField.addEventListener("click", () => nameField.focus());
  nameField.addEventListener("keydown", (event) => {
    if (event.key === "Backspace") {
      event.preventDefault();
      value = value.slice(0, -1);
      sync();
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      submit();
      return;
    }

    if (event.key.length !== 1 || value.length >= 24) return;

    const character = event.key;
    if (!/^[A-Za-z0-9 ._-]$/.test(character)) return;

    event.preventDefault();
    value += character;
    sync();
  });

  nameField.addEventListener("blur", submit);

  sync();
  nameField.focus();
}

function renderEndLeaderboard(
  theme: GameTheme,
  entries: LeaderboardEntry[],
  result: RoundResult,
  rank: number,
  qualifies: boolean,
  lastName: string,
  submitState: "idle" | "submitting" | "done" | "error"
) {
  if (!qualifies || submitState === "done") {
    return entries.slice(0, 10).map((entry) => renderEndRow(theme, entry.name, entry.score)).join("");
  }

  const rows = [...entries.slice(0, rank - 1), result, ...entries.slice(rank - 1)].slice(0, 10);

  return `
    <form class="highscore-form" data-score-form>
        ${rows.map((entry) => {
          if (entry === result) {
            return `
              <div class="end-row end-row-current">
                <input type="hidden" name="name" value="${escapeAttribute(lastName)}" />
                <div class="name-entry" role="textbox" aria-label="Name" tabindex="0" data-name-entry data-disabled="${submitState === "submitting" ? "true" : "false"}">
                  ${renderBitmapText(lastName || "_", theme)}
                </div>
                <strong class="current-score">${renderBitmapText(submitState === "submitting" ? "..." : String(result.score), theme)}</strong>
              </div>
            `;
          }

          return renderEndRow(theme, (entry as LeaderboardEntry).name, (entry as LeaderboardEntry).score);
        }).join("")}
    </form>
  `;
}

function renderEndRow(theme: GameTheme, name: string, score: number) {
  return `
    <div class="end-row">
      <b>${renderBitmapText(name, theme)}</b>
      <strong>${renderBitmapText(String(score), theme)}</strong>
    </div>
  `;
}

function renderMenuTargetDetail(theme: GameTheme) {
  const chicken = theme.targets.find((target) => target.id === "bigChicken")?.asset.url;
  if (!chicken) return "";

  return `
    <div class="menu-target-detail" aria-hidden="true">
      <span class="menu-target-chicken">
        <img src="${chicken}" alt="" />
      </span>
      ${Array.from({ length: 7 }, (_, index) => (
        `<span class="menu-target-hole menu-target-hole-${index + 1}"></span>`
      )).join("")}
    </div>
  `;
}

function effectiveLeaderboardEntries(theme: GameTheme, entries: LeaderboardEntry[]) {
  return [...entries, ...originalLeaderboardEntries(theme)]
    .sort(scoreRank)
    .slice(0, 10);
}

function originalLeaderboardEntries(theme: GameTheme): LeaderboardEntry[] {
  return [
    ["Frank", 1000],
    ["Helena", 800],
    ["Varlon", 780],
    ["Varlon", 695],
    ["Varlon", 655],
    ["Gerhard", 600],
    ["Michael", 400],
    ["Marie", 300]
  ].map(([name, score], index) => ({
    id: -(index + 1),
    name: String(name),
    score: Number(score),
    accuracy: 0,
    hits: 0,
    misses: 0,
    shotsFired: 0,
    reloads: 0,
    durationMs: theme.round.durationMs,
    themeId: theme.id,
    themeVersion: theme.version,
    targetHits: {},
    createdAt: `2005-06-29T00:00:0${index}.000Z`
  }));
}

function renderBitmapText(value: string, _theme: GameTheme) {
  return `
    <span class="bitmap-text" aria-label="${escapeAttribute(value)}">
      ${escapeHtml(value)}
    </span>
  `;
}

function rankForResult(entries: LeaderboardEntry[], result: RoundResult) {
  const marker = { ...result, id: Number.MIN_SAFE_INTEGER, name: "", createdAt: "" };
  const sorted = [...entries, marker].sort(scoreRank);

  return sorted.findIndex((entry) => entry === marker) + 1;
}

function scoreRank(a: Pick<LeaderboardEntry, "score" | "createdAt">, b: Pick<LeaderboardEntry, "score" | "createdAt">) {
  return b.score - a.score || a.createdAt.localeCompare(b.createdAt);
}

function applyTheme(root: HTMLElement, theme: GameTheme) {
  const style = root.style;
  const colors = theme.ui.colors;
  style.setProperty("--page", colors.page);
  style.setProperty("--panel", colors.panel);
  style.setProperty("--panel-strong", colors.panelStrong);
  style.setProperty("--text", colors.text);
  style.setProperty("--muted", colors.muted);
  style.setProperty("--accent", colors.accent);
  style.setProperty("--accent-alt", colors.accentAlt);
  style.setProperty("--danger", colors.danger);
  style.setProperty("--success", colors.success);
  style.setProperty("--line", colors.line);
  style.setProperty("--font", theme.ui.fontFamily);
  style.setProperty("--loading-card", `url("${theme.ui.loadingUrl}")`);
  style.setProperty("--title-card", `url("${theme.ui.titleCardUrl}")`);
  style.setProperty("--highscore-card", `url("${theme.ui.highscoreUrl}")`);
  style.setProperty("--menu-hole", `url("${theme.effects.treeHoleUrl}")`);
}

function escapeHtml(value: string) {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function escapeAttribute(value: string) {
  return escapeHtml(value).replaceAll("`", "&#096;");
}
